import {
  AlertTriangle,
  ArrowLeft,
  Bot,
  Check,
  CheckCircle2,
  ChevronDown,
  CircleOff,
  Clock3,
  Copy,
  Cpu,
  Download,
  FileVideo2,
  History,
  ImagePlus,
  LoaderCircle,
  LockKeyhole,
  ListPlus,
  ListVideo,
  LogOut,
  PencilLine,
  Plus,
  RefreshCw,
  RotateCcw,
  Save,
  Server,
  Share2,
  Sparkles,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError, api } from "./api";
import { SocialAccountsManager } from "./SocialAccountsManager";
import { SocialPublisher } from "./SocialPublisher";
import { LocalH3ControlPlane } from "./LocalH3ControlPlane";
import { PromptSyntaxEditor } from "./PromptSyntaxEditor";
import { UNSUPPORTED_PROMPT_MARKER, type DashboardStatus, type Job, type JobDraft, type JobResult, type JobStatus, type ReferenceIntent, type SocialAccounts } from "./types";

type ProfileOption = {
  value: string;
  label: string;
  detail: string;
  eta: string;
  quality: string;
  bestFor: string;
};

export type ContinuityShot = {
  name: string;
  prompt: string;
  duration_seconds: number;
  seed: number;
};

export type ContinuityBundle = {
  schema_version: "h3-continuity-chain/v1";
  title?: string;
  continuity_mode: "previous_result_last_frame";
  execution_contract: {
    generation_only: true;
    records_keep: false;
    publication_approved: false;
  };
  shots: ContinuityShot[];
};

export type PromptTranslationSection = {
  key: "reference" | "direction" | "sound" | "music";
  label: string;
  field: string | null;
  content: string;
};

const TRANSLATION_FIELDS = [
  { key: "direction" as const, label: "장면 · 동작 · 카메라", field: "integrated_multimodal_description" },
  { key: "sound" as const, label: "현장음 · 효과음", field: "overall_soundscape" },
  { key: "music" as const, label: "배경 음악", field: "non_diegetic_music" },
];

export function splitPromptTranslation(value: string): PromptTranslationSection[] {
  const markers = TRANSLATION_FIELDS.map((definition) => ({
    ...definition,
    index: value.indexOf(`${definition.field}:`),
  })).filter((definition) => definition.index >= 0).sort((a, b) => a.index - b.index);
  if (!markers.length) return [{ key: "reference", label: "한국어 해석", field: null, content: value.trim() }];

  const sections: PromptTranslationSection[] = [];
  const lead = value.slice(0, markers[0].index).trim();
  if (lead) sections.push({ key: "reference", label: "참조 기준", field: null, content: lead });
  markers.forEach((marker, index) => {
    const start = marker.index + marker.field.length + 1;
    const end = markers[index + 1]?.index ?? value.length;
    sections.push({
      key: marker.key,
      label: marker.label,
      field: marker.field,
      content: value.slice(start, end).trim(),
    });
  });
  return sections.filter((section) => section.content);
}

export type TranslationTimelineBeat = { range: string | null; content: string };

export function splitTranslationTimeline(value: string): TranslationTimelineBeat[] {
  const rangePattern = /(\d+(?:\.\d+)?초부터\s+(?:\d+(?:\.\d+)?초까지|끝까지))[,，:]?/g;
  const matches = [...value.matchAll(rangePattern)];
  if (!matches.length) return [{ range: null, content: value.trim() }];
  const beats: TranslationTimelineBeat[] = [];
  const lead = value.slice(0, matches[0].index).trim();
  if (lead) beats.push({ range: null, content: lead });
  matches.forEach((match, index) => {
    const start = (match.index ?? 0) + match[0].length;
    const end = matches[index + 1]?.index ?? value.length;
    beats.push({ range: match[1], content: value.slice(start, end).trim() });
  });
  return beats.filter((beat) => beat.content);
}

export function parseContinuityBundle(text: string): ContinuityBundle {
  const value = JSON.parse(text) as Partial<ContinuityBundle>;
  if (value.schema_version !== "h3-continuity-chain/v1" ||
      value.continuity_mode !== "previous_result_last_frame") {
    throw new Error("contents-maker의 h3-continuity-chain/v1 번들만 사용할 수 있습니다.");
  }
  if (value.execution_contract?.generation_only !== true ||
      value.execution_contract?.records_keep !== false ||
      value.execution_contract?.publication_approved !== false) {
    throw new Error("이 번들은 생성 전용이어야 하며 KEEP이나 게시 승인을 포함할 수 없습니다.");
  }
  if (!Array.isArray(value.shots) || value.shots.length < 2 || value.shots.length > 8) {
    throw new Error("연속 샷 번들은 2–8개 shots 배열이어야 합니다.");
  }
  const shots = value.shots.map((shot, index) => {
    const name = String(shot.name || `shot-${index + 1}`).trim();
    const prompt = String(shot.prompt || "").trim();
    const duration = Number(shot.duration_seconds);
    const seed = Number(shot.seed);
    if (!prompt.startsWith("For the target video, at 0.00 seconds into the target video, <Picture 1> (from [Shot 1]) is fully referenced.") ||
        !prompt.includes("integrated_multimodal_description:") ||
        !prompt.includes("overall_soundscape:") ||
        !prompt.includes("non_diegetic_music:")) {
      throw new Error(`샷 ${index + 1}의 H3 프롬프트가 완전하지 않습니다.`);
    }
    if (!Number.isInteger(duration) || duration < 3 || duration > 15) {
      throw new Error(`샷 ${index + 1} 길이는 3–15초 정수여야 합니다.`);
    }
    if (!Number.isInteger(seed) || seed < 0 || seed > 4294967295) {
      throw new Error(`샷 ${index + 1} 시드가 올바르지 않습니다.`);
    }
    return { name, prompt, duration_seconds: duration, seed };
  });
  return {
    schema_version: value.schema_version,
    title: value.title,
    continuity_mode: value.continuity_mode,
    execution_contract: value.execution_contract,
    shots,
  };
}

export const PROFILE_OPTIONS: readonly ProfileOption[] = [
  {
    value: "turbo",
    label: "가속 최적화",
    detail: "권장 · 품질과 속도 균형",
    eta: "일반 생성",
    quality: "게시본은 1080×1920 후처리",
    bestFor: "대부분의 이미지 기반 숏츠",
  },
  {
    value: "final",
    label: "비가속 최고품질",
    detail: "가속 노드 미사용 · 매우 느림",
    eta: "실측 10분 이상 가능",
    quality: "선택된 한 컷에만 사용",
    bestFor: "가속 결과에 명확한 결함이 남은 최종 재시도",
  },
];

export const REFERENCE_VIDEO_PROFILES: readonly ProfileOption[] = [
  {
    value: "ref2va",
    label: "가속 최적화",
    detail: "공식 Ref2VA 20-step · 기본",
    eta: "5090 warm 실측 약 9–15분 / 10초",
    quality: "게시본은 1080×1920 후처리",
    bestFor: "개체 교체와 장면 재구성 모두의 기본 경로",
  },
  {
    value: "ref2va-final",
    label: "비가속 정체성 강화",
    detail: "Sage 미사용 · Picture 입력 강화",
    eta: "30분 상한 · 선택적",
    quality: "identity max",
    bestFor: "가속 결과에서 Picture 외형이 반복적으로 약할 때만",
  },
];

const LEGACY_PROFILE_OPTIONS: readonly ProfileOption[] = [
  { value: "turbo-draft", label: "이전 빠른 후보", detail: "호환 모드", eta: "이전 작업", quality: "", bestFor: "새 작업에서는 숨김" },
  { value: "draft", label: "이전 비가속 후보", detail: "호환 모드", eta: "이전 작업", quality: "", bestFor: "새 작업에서는 숨김" },
  { value: "balanced", label: "이전 안정형", detail: "호환 모드", eta: "이전 작업", quality: "", bestFor: "새 작업에서는 숨김" },
  { value: "native", label: "이전 Native", detail: "호환 모드", eta: "이전 작업", quality: "", bestFor: "새 작업에서는 숨김" },
];

export function visibleProfilesForJob(referenceKind: Job["reference_kind"], currentProfile: string): readonly ProfileOption[] {
  const visible = referenceKind === "video" ? REFERENCE_VIDEO_PROFILES : PROFILE_OPTIONS;
  if (visible.some((profile) => profile.value === currentProfile)) return visible;
  const legacy = LEGACY_PROFILE_OPTIONS.find((profile) => profile.value === currentProfile);
  return legacy ? [...visible, legacy] : visible;
}

const PROFILE_DEFAULTS: Record<string, Pick<JobDraft, "width" | "height" | "frames">> = {
  "turbo-draft": { width: 480, height: 864, frames: 192 },
  turbo: { width: 544, height: 960, frames: 192 },
  draft: { width: 480, height: 864, frames: 192 },
  balanced: { width: 544, height: 960, frames: 192 },
  final: { width: 576, height: 1024, frames: 192 },
  native: { width: 768, height: 1344, frames: 192 },
  ref2va: { width: 544, height: 960, frames: 192 },
  "ref2va-final": { width: 576, height: 1024, frames: 192 },
};

const IMAGE_ACCEPT = "image/jpeg,image/png,image/webp";
const VIDEO_ACCEPT = "video/mp4,video/webm,video/quicktime,video/x-m4v";
const MAX_IMAGE_BYTES = 15 * 1024 * 1024;
const MAX_VIDEO_BYTES = 95 * 1024 * 1024;
const MAX_REFERENCE_IMAGES = 9;

const STATUS_META: Record<JobStatus, { label: string; tone: string }> = {
  draft: { label: "초안", tone: "neutral" },
  ready: { label: "준비됨", tone: "ready" },
  queued: { label: "H3 시작 대기", tone: "active" },
  preparing: { label: "환경 준비", tone: "active" },
  rendering: { label: "생성 중", tone: "active" },
  uploading: { label: "결과 저장", tone: "active" },
  completed: { label: "완료", tone: "success" },
  failed: { label: "실패", tone: "danger" },
  interrupted: { label: "중단됨", tone: "warning" },
};

const ACTIVE_STATUSES = new Set<JobStatus>(["queued", "preparing", "rendering", "uploading"]);
const EDITABLE_STATUSES = new Set<JobStatus>(["draft", "ready", "failed", "interrupted"]);

export function readableJobError(error: string | null, status: JobStatus): string {
  const fallback = status === "interrupted"
    ? "생성을 중단했습니다. 입력은 그대로 보존되어 있습니다."
    : "생성을 마치지 못했습니다. 입력은 그대로 보존되어 있습니다.";
  if (!error?.trim()) return fallback;
  const text = error.trim();
  if (text.includes("execution_interrupted") || text.includes("Generation was interrupted")) {
    return "사용자 요청으로 생성을 중단했습니다. 입력은 그대로 보존되어 있습니다.";
  }
  if (text.includes("did not finish after") || text.includes("render timed out")) {
    return "설정된 시간 안에 생성되지 않아 중단했습니다. 입력은 그대로 보존되어 있습니다.";
  }
  if (text.startsWith("{") || text.startsWith("[") || text.length > 700) {
    return fallback;
  }
  return text;
}

function randomSeed(): number {
  return crypto.getRandomValues(new Uint32Array(1))[0];
}

function emptyDraft(): JobDraft {
  return {
    name: "",
    prompt: "",
    profile: "turbo",
    ...PROFILE_DEFAULTS.turbo,
    seed: randomSeed(),
    reference_intent: "reference_generation",
  };
}

function draftFromJob(job: Job): JobDraft {
  return {
    name: job.name,
    prompt: job.prompt,
    profile: job.profile,
    width: job.width,
    height: job.height,
    frames: job.frames,
    seed: job.seed,
    reference_intent: job.reference_intent || "reference_generation",
  };
}

function formatTime(timestamp: number | null): string {
  if (!timestamp) return "—";
  return new Intl.DateTimeFormat("ko-KR", {
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(timestamp * 1000));
}

function formatBytes(bytes: number | null): string {
  if (!bytes) return "";
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatDuration(seconds: number | null): string {
  if (seconds == null) return "길이 확인 전";
  return `${seconds.toFixed(seconds < 10 ? 1 : 0)}초`;
}

export function h3FramesForDuration(seconds: number): number {
  const target = Math.max(124, Math.ceil(seconds * 24));
  return Math.min(362, Math.max(124, Math.ceil((target - 5) / 17) * 17 + 5));
}

export function multiCutCount(seconds: number): number {
  return Math.min(8, Math.ceil(Math.max(16, seconds || 16) / 10));
}

function formatElapsed(seconds: number | null): string {
  if (seconds == null) return "측정 전";
  const rounded = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(rounded / 60);
  const remainder = rounded % 60;
  return minutes ? `${minutes}분 ${remainder}초` : `${remainder}초`;
}

type TimedJob = Pick<
  Job,
  "status" | "execution_started_at" | "generation_started_at" | "generation_seconds" | "total_seconds"
>;

export type JobTiming =
  | { kind: "live"; label: string; startedAt: number }
  | { kind: "complete"; generationSeconds: number | null; totalSeconds: number | null };

export function resolveJobTiming(job: TimedJob): JobTiming | null {
  if (job.status === "preparing" && job.execution_started_at != null) {
    return { kind: "live", label: "환경 준비", startedAt: job.execution_started_at };
  }
  if (job.status === "rendering" && job.generation_started_at != null) {
    return { kind: "live", label: "H3 생성", startedAt: job.generation_started_at };
  }
  if (job.status === "uploading" && job.execution_started_at != null) {
    return { kind: "live", label: "전체 처리", startedAt: job.execution_started_at };
  }
  if (job.status === "completed" && (job.generation_seconds != null || job.total_seconds != null)) {
    return {
      kind: "complete",
      generationSeconds: job.generation_seconds,
      totalSeconds: job.total_seconds,
    };
  }
  return null;
}

function LiveElapsed({ label, startedAt }: { label: string; startedAt: number }) {
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));

  useEffect(() => {
    const tick = () => setNow(Math.floor(Date.now() / 1000));
    tick();
    const timer = window.setInterval(tick, 1000);
    const syncWhenVisible = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", syncWhenVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", syncWhenVisible);
    };
  }, [startedAt]);

  const elapsed = Math.max(0, now - startedAt);
  return (
    <time className="job-timing live" dateTime={`PT${elapsed}S`} title={`${label} 경과시간`}>
      {label} {formatElapsed(elapsed)} 경과
    </time>
  );
}

function JobTimingLabel({ job, detail = false, separator = false }: { job: TimedJob; detail?: boolean; separator?: boolean }) {
  const timing = resolveJobTiming(job);
  if (!timing) return null;

  const content = timing.kind === "live" ? (
    <LiveElapsed label={timing.label} startedAt={timing.startedAt} />
  ) : (
    <span className="job-timing complete" title="완료 시 기록된 처리시간">
      {timing.generationSeconds != null ? `H3 ${formatElapsed(timing.generationSeconds)}` : `전체 ${formatElapsed(timing.totalSeconds)}`}
      {detail && timing.generationSeconds != null && timing.totalSeconds != null ? ` · 전체 ${formatElapsed(timing.totalSeconds)}` : ""}
    </span>
  );

  return separator ? <><span className="timing-separator" aria-hidden="true">·</span>{content}</> : content;
}

function formatRuntimeElapsed(startedAt: number): string {
  const elapsed = Math.max(0, Math.floor(Date.now() / 1000) - startedAt);
  const days = Math.floor(elapsed / 86400);
  const hours = Math.floor((elapsed % 86400) / 3600);
  const minutes = Math.floor((elapsed % 3600) / 60);
  if (days) return `${days}일 ${hours}시간`;
  if (hours) return `${hours}시간 ${minutes}분`;
  return `${minutes}분`;
}

function formatSessionCost(hourlyUsd: number | null, startedAt: number): string {
  if (hourlyUsd == null) return "요금 확인 전";
  const elapsedHours = Math.max(0, Date.now() / 1000 - startedAt) / 3600;
  const cost = hourlyUsd * elapsedHours;
  return cost < 0.01 ? "약 <$0.01" : `약 $${cost.toFixed(2)}`;
}

type GpuStatusTone = "off" | "starting" | "ready" | "working" | "unknown";

export function resolveGpuStatus(
  runtime: DashboardStatus["runtime"],
  bridgeOnline: boolean,
  activeJobs: number,
): { tone: GpuStatusTone; label: string; detail: string } {
  if (!runtime) {
    return bridgeOnline
      ? { tone: "off", label: "Vast 5090 · 꺼짐", detail: "현재 GPU 과금 없음" }
      : { tone: "unknown", label: "Vast 5090 · 확인 불가", detail: "Mac 브리지 오프라인" };
  }
  if (runtime.stale) {
    return { tone: "unknown", label: "Vast 5090 · 확인 지연", detail: "마지막 세션 상태 표시 중" };
  }
  if (["error", "failed", "offline", "stopped", "exited"].includes(runtime.status)) {
    return { tone: "unknown", label: "Vast 5090 · 상태 확인", detail: `인스턴스 ${runtime.status}` };
  }
  if (!runtime.ready) {
    const detail = runtime.status === "running" ? "모델·ComfyUI 준비 중" : `인스턴스 ${runtime.status}`;
    return { tone: "starting", label: "Vast 5090 · 준비 중", detail };
  }
  if (activeJobs > 0) {
    return { tone: "working", label: "Vast 5090 · 생성 중", detail: `${activeJobs}개 작업 활성` };
  }
  return { tone: "ready", label: "Vast 5090 · 사용 가능", detail: "대기 작업을 받을 수 있음" };
}

type VideoMetadata = { duration: number; width: number; height: number };

export function ref2vaCanvasForSource(width: number, height: number): { width: number; height: number } {
  const ratio = width / height;
  let targetWidth = ratio <= 1 ? Math.round((1024 * ratio) / 32) * 32 : 1024;
  let targetHeight = ratio <= 1 ? 1024 : Math.round((1024 / ratio) / 32) * 32;
  targetWidth = Math.max(256, Math.min(1344, targetWidth));
  targetHeight = Math.max(256, Math.min(1344, targetHeight));
  while (targetWidth * targetHeight > 768 * 1344) {
    if (targetWidth >= targetHeight) targetWidth -= 32;
    if (targetHeight >= targetWidth) targetHeight -= 32;
  }
  return { width: targetWidth, height: targetHeight };
}

function probeVideoMetadata(file: File): Promise<VideoMetadata> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    const cleanup = () => {
      video.removeAttribute("src");
      video.load();
      URL.revokeObjectURL(url);
    };
    const timeout = window.setTimeout(() => {
      cleanup();
      reject(new Error("영상 정보를 읽는 데 시간이 너무 오래 걸립니다."));
    }, 15_000);
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      window.clearTimeout(timeout);
      const duration = video.duration;
      const width = video.videoWidth;
      const height = video.videoHeight;
      cleanup();
      if (!Number.isFinite(duration) || !width || !height) {
        reject(new Error("영상 길이와 원본 해상도를 확인할 수 없습니다."));
        return;
      }
      resolve({ duration, width, height });
    };
    video.onerror = () => {
      window.clearTimeout(timeout);
      cleanup();
      reject(new Error("이 영상을 읽을 수 없습니다. MP4(H.264) 또는 WebM으로 다시 저장해 주세요."));
    };
    video.src = url;
  });
}

function LocalImageThumbnail({ file, alt }: { file: File; alt: string }) {
  const [source, setSource] = useState("");
  useEffect(() => {
    const url = URL.createObjectURL(file);
    setSource(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  return source ? <img src={source} alt={alt} /> : null;
}

function checkedReferenceImages(files: File[], remaining: number): File[] {
  if (files.length > remaining) {
    throw new Error(`보조 이미지는 최대 ${MAX_REFERENCE_IMAGES}장까지 추가할 수 있습니다.`);
  }
  for (const file of files) {
    if (!IMAGE_ACCEPT.split(",").includes(file.type)) {
      throw new Error("보조 이미지는 JPG, PNG, WebP만 사용할 수 있습니다.");
    }
    if (file.size <= 0 || file.size > MAX_IMAGE_BYTES) {
      throw new Error("보조 이미지는 각각 15MB 이하여야 합니다.");
    }
  }
  return files;
}

function useMessage() {
  const [message, setMessage] = useState("");
  const timer = useRef<number | null>(null);
  const announce = useCallback((value: string) => {
    setMessage(value);
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setMessage(""), 5000);
  }, []);
  useEffect(() => () => {
    if (timer.current) window.clearTimeout(timer.current);
  }, []);
  return { message, announce };
}

function LoginScreen({ onLogin }: { onLogin: () => void }) {
  const [key, setKey] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.login(key);
      setKey("");
      onLogin();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "로그인하지 못했습니다.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login-shell">
      <section className="login-panel" aria-labelledby="login-title">
        <div className="brand-mark" aria-hidden="true"><Sparkles size={20} /></div>
        <p className="eyebrow">PRIVATE PRODUCTION QUEUE</p>
        <h1 id="login-title">H3 제작 큐</h1>
        <p className="login-copy">우리 팀의 레퍼런스 영상 작업을 준비하고 한 번에 생성합니다.</p>
        <form onSubmit={submit} className="login-form">
          <label htmlFor="shared-key">공유 비밀번호</label>
          <div className="input-with-icon">
            <LockKeyhole size={18} aria-hidden="true" />
            <input
              id="shared-key"
              type="password"
              value={key}
              onChange={(event) => setKey(event.target.value)}
              autoComplete="current-password"
              minLength={32}
              required
              autoFocus
            />
          </div>
          {error && <p className="form-error" role="alert">{error}</p>}
          <button className="button primary wide" disabled={busy}>
            {busy ? <LoaderCircle className="spin" size={18} /> : <LockKeyhole size={18} />}
            {busy ? "확인 중" : "들어가기"}
          </button>
        </form>
        <p className="login-note">비밀번호는 브라우저 저장소에 남지 않습니다.</p>
        <a className="agent-guide-login" href="/AGENTS.md" target="_blank" rel="noreferrer"><Bot size={15} />AI 에이전트용 연결 안내</a>
      </section>
    </main>
  );
}

interface NewJobDialogProps {
  onClose: () => void;
  onCreated: (job: Job) => void;
}

function NewJobDialog({ onClose, onCreated }: NewJobDialogProps) {
  const [draft, setDraft] = useState<JobDraft>(emptyDraft);
  const [referenceKind, setReferenceKind] = useState<"image" | "video">("image");
  const [reference, setReference] = useState<File | null>(null);
  const [referenceDuration, setReferenceDuration] = useState<number | null>(null);
  const [referenceDimensions, setReferenceDimensions] = useState<{ width: number; height: number } | null>(null);
  const [referenceImages, setReferenceImages] = useState<File[]>([]);
  const [preview, setPreview] = useState<string | null>(null);
  const [checkingReference, setCheckingReference] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const referenceCheck = useRef(0);
  const referenceInput = useRef<HTMLInputElement>(null);
  const referenceImagesInput = useRef<HTMLInputElement>(null);

  useEffect(() => () => {
    if (preview) URL.revokeObjectURL(preview);
  }, [preview]);

  function changeReferenceKind(kind: "image" | "video") {
    referenceCheck.current += 1;
    setReferenceKind(kind);
    setReference(null);
    setReferenceDuration(null);
    setReferenceDimensions(null);
    setReferenceImages([]);
    setPreview(null);
    setCheckingReference(false);
    setError("");
    setDraft((current) => ({
      ...current,
      profile: kind === "video" ? "ref2va" : "turbo",
      ...PROFILE_DEFAULTS[kind === "video" ? "ref2va" : "turbo"],
      reference_intent: kind === "video" ? "subject_replacement" : "reference_generation",
    }));
  }

  function chooseReferenceImages(files?: FileList | null) {
    if (!files?.length) return;
    try {
      const next = checkedReferenceImages(
        Array.from(files),
        MAX_REFERENCE_IMAGES - referenceImages.length,
      );
      setReferenceImages((current) => [...current, ...next]);
      setError("");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "보조 이미지를 추가하지 못했습니다.");
    } finally {
      if (referenceImagesInput.current) referenceImagesInput.current.value = "";
    }
  }

  async function chooseReference(file?: File) {
    if (!file) return;
    const expected = referenceKind === "video" ? "video/" : "image/";
    const limit = referenceKind === "video" ? MAX_VIDEO_BYTES : MAX_IMAGE_BYTES;
    if (!file.type.startsWith(expected)) {
      setError(referenceKind === "video" ? "MP4, WebM, MOV 영상을 선택하세요." : "JPG, PNG, WebP 이미지를 선택하세요.");
      return;
    }
    if (file.size > limit) {
      setError(referenceKind === "video" ? "레퍼런스 영상은 95MB 이하여야 합니다." : "레퍼런스 이미지는 15MB 이하여야 합니다.");
      return;
    }
    const check = referenceCheck.current + 1;
    referenceCheck.current = check;
    setReference(file);
    setReferenceDuration(null);
    setPreview(URL.createObjectURL(file));
    setError("");
    if (!draft.name) setDraft((current) => ({ ...current, name: file.name.replace(/\.[^.]+$/, "") }));
    if (referenceKind === "video") {
      setCheckingReference(true);
      try {
        const metadata = await probeVideoMetadata(file);
        const duration = metadata.duration;
        if (referenceCheck.current !== check) return;
        if (duration < 2 || duration > 15) {
          setError(`레퍼런스 영상은 2–15초여야 합니다. 선택한 영상은 ${formatDuration(duration)}입니다.`);
          return;
        }
        setReferenceDuration(duration);
        setReferenceDimensions({ width: metadata.width, height: metadata.height });
        const canvas = ref2vaCanvasForSource(metadata.width, metadata.height);
        setDraft((current) => ({
          ...current,
          frames: h3FramesForDuration(duration),
          ...(current.reference_intent === "subject_replacement" ? canvas : {}),
        }));
      } catch (reason) {
        if (referenceCheck.current === check) {
          setError(reason instanceof Error ? reason.message : "영상 정보를 확인하지 못했습니다.");
        }
      } finally {
        if (referenceCheck.current === check) setCheckingReference(false);
      }
    }
  }

  function setProfile(profile: string) {
    setDraft((current) => ({
      ...current,
      profile,
      ...(referenceKind === "video" && current.reference_intent === "subject_replacement"
        ? {}
        : PROFILE_DEFAULTS[profile]),
    }));
  }

  async function save(status: "draft" | "ready") {
    if (!reference) {
      setError("레퍼런스 이미지 또는 영상을 먼저 선택하세요.");
      return;
    }
    if (referenceKind === "video" && referenceDuration == null) {
      setError("영상 길이 확인이 끝난 뒤 저장해 주세요.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const job = await api.createJob(
        draft,
        reference,
        status,
        referenceDuration,
        referenceKind === "video" ? referenceImages : [],
        referenceDimensions,
      );
      onCreated(job);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "작업을 만들지 못했습니다.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.currentTarget === event.target && !busy) onClose();
    }}>
      <section className="job-dialog" role="dialog" aria-modal="true" aria-labelledby="new-job-title">
        <header className="dialog-header">
          <div>
            <p className="eyebrow">NEW JOB</p>
            <h2 id="new-job-title">새 영상 준비</h2>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="닫기" disabled={busy}><X /></button>
        </header>
        <div className="dialog-grid">
          <div className="media-upload-group">
            <fieldset className="media-kind-selector">
              <legend>레퍼런스 유형</legend>
              <label><input type="radio" name="reference-kind" value="image" checked={referenceKind === "image"} onChange={() => changeReferenceKind("image")} /><ImagePlus size={17} /><span><strong>이미지</strong><small>첫 프레임 고정</small></span></label>
              <label><input type="radio" name="reference-kind" value="video" checked={referenceKind === "video"} onChange={() => changeReferenceKind("video")} /><FileVideo2 size={17} /><span><strong>영상</strong><small>동작·인물 참조</small></span></label>
            </fieldset>
            <input ref={referenceInput} id="new-reference-file" className="visually-hidden" type="file" accept={referenceKind === "video" ? VIDEO_ACCEPT : IMAGE_ACCEPT} onChange={(event) => void chooseReference(event.target.files?.[0])} />
            {preview ? (
              <div className="reference-preview-stack">
                <div className="drop-zone has-media">
                  {referenceKind === "video"
                    ? <video src={preview} controls playsInline preload="metadata">브라우저에서 영상을 재생할 수 없습니다.</video>
                    : <img src={preview} alt="선택한 레퍼런스 미리보기" />}
                </div>
                <div className="selected-media-meta">
                  <div><strong>{reference?.name}</strong><small>{formatBytes(reference?.size ?? null)}{referenceKind === "video" ? ` · ${checkingReference ? "길이 확인 중" : formatDuration(referenceDuration)}` : ""}</small></div>
                  <button className="button subtle compact" type="button" onClick={() => {
                    if (!referenceInput.current) return;
                    referenceInput.current.value = "";
                    referenceInput.current.click();
                  }}><Upload size={16} />{referenceKind === "video" ? "영상 바꾸기" : "이미지 바꾸기"}</button>
                </div>
              </div>
            ) : (
              <label className="drop-zone" htmlFor="new-reference-file" aria-describedby="reference-upload-help">
                <span>{referenceKind === "video" ? <FileVideo2 size={28} /> : <ImagePlus size={28} />}<strong>{referenceKind === "video" ? "레퍼런스 영상 선택" : "레퍼런스 이미지 선택"}</strong><small id="reference-upload-help">{referenceKind === "video" ? "MP4 · WebM · MOV, 2–15초, 최대 95MB" : "JPG · PNG · WebP, 최대 15MB"}</small></span>
              </label>
            )}
            {referenceKind === "video" && (
              <section className="aux-reference-picker" aria-labelledby="new-aux-reference-title">
                <div className="aux-reference-heading">
                  <div><strong id="new-aux-reference-title">보조 이미지</strong><small>제품·로고·의상 등, 선택 사항</small></div>
                  <span>{referenceImages.length} / {MAX_REFERENCE_IMAGES}</span>
                </div>
                <input
                  ref={referenceImagesInput}
                  id="new-reference-images"
                  className="visually-hidden"
                  type="file"
                  accept={IMAGE_ACCEPT}
                  multiple
                  onChange={(event) => chooseReferenceImages(event.target.files)}
                />
                <div className="aux-reference-grid">
                  {referenceImages.map((file, index) => (
                    <div className="aux-reference-card" key={`${file.name}-${file.lastModified}-${index}`}>
                      <LocalImageThumbnail file={file} alt={`선택한 Picture ${index + 1}`} />
                      <span>&lt;Picture {index + 1}&gt;</span>
                      <button
                        type="button"
                        className="aux-remove"
                        onClick={() => setReferenceImages((current) => current.filter((_, candidate) => candidate !== index))}
                        aria-label={`Picture ${index + 1} 삭제`}
                      ><X size={14} /></button>
                    </div>
                  ))}
                  {referenceImages.length < MAX_REFERENCE_IMAGES && (
                    <label className="aux-add" htmlFor="new-reference-images">
                      <ImagePlus size={18} /><span>이미지 추가</span>
                    </label>
                  )}
                </div>
                <p>선택 순서대로 <code>&lt;Picture 1&gt;</code>, <code>&lt;Picture 2&gt;</code>가 됩니다.</p>
              </section>
            )}
          </div>
          <div className="dialog-fields">
            <label>작업 이름<input value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} placeholder="예: 고양이 카페 반전" required /></label>
            {referenceKind === "video" && <ReferenceIntentSelector value={draft.reference_intent} onChange={(reference_intent) => setDraft((current) => ({
              ...current,
              reference_intent,
              ...(reference_intent === "subject_replacement" && referenceDimensions
                ? ref2vaCanvasForSource(referenceDimensions.width, referenceDimensions.height)
                : {}),
            }))} />}
            <label>영상 프롬프트<textarea value={draft.prompt} onChange={(event) => setDraft({ ...draft, prompt: event.target.value })} rows={8} placeholder={referenceKind === "video" ? draft.reference_intent === "subject_replacement" ? "Video 1의 편집 타임라인은 유지하고 Picture의 외형만 원본 대상 역할에 attribute_transfer 하세요." : "Video 1의 동작·카메라·컷 리듬을 참고해 새 장면을 설계하세요." : "장면, 주체의 동작, 카메라, 조명과 사운드를 시간 순서대로 적으세요."} required /></label>
            <label>실행 설정<select value={draft.profile} onChange={(event) => setProfile(event.target.value)}>{(referenceKind === "video" ? REFERENCE_VIDEO_PROFILES : PROFILE_OPTIONS).map((profile) => <option key={profile.value} value={profile.value}>{profile.label} — {profile.detail}</option>)}</select></label>
          {referenceKind === "video" && <p className="dialog-field-note"><FileVideo2 size={15} /><span>{draft.reference_intent === "subject_replacement" ? <><code>&lt;Video 1&gt;</code>의 컷·카메라·동작 역할을 유지하고 <code>&lt;Picture N&gt;</code> 외형만 <code>attribute_transfer</code>합니다. 다중 컷은 실제 타임스탬프를 모두 적습니다.</> : <><code>&lt;Video 1&gt;</code>의 동작·카메라·컷 리듬을 참고해 새 장면을 생성합니다.</>}</span></p>}
          </div>
        </div>
        {error && <p className="form-error" role="alert">{error}</p>}
        <footer className="dialog-actions">
          <button className="button subtle" onClick={() => save("draft")} disabled={busy || checkingReference}><Save size={17} />초안 저장</button>
          <button className="button primary" onClick={() => save("ready")} disabled={busy || checkingReference}>{busy || checkingReference ? <LoaderCircle className="spin" size={17} /> : <Check size={17} />}{checkingReference ? "영상 확인 중" : "준비 완료"}</button>
        </footer>
      </section>
    </div>
  );
}

function StatusBadge({ status, provider: _provider }: { status: JobStatus; provider?: Job["execution_provider"] }) {
  const meta = STATUS_META[status] || { label: status, tone: "neutral" };
  return <span className={`status-badge ${meta.tone}`}><span className="status-dot" />{meta.label}</span>;
}

function ReferenceIntentSelector({
  value,
  onChange,
  disabled = false,
}: {
  value: ReferenceIntent;
  onChange: (value: ReferenceIntent) => void;
  disabled?: boolean;
}) {
  return (
    <fieldset className="reference-intent-selector">
      <legend>영상 작업</legend>
      <label>
        <input type="radio" name="reference-intent" checked={value === "subject_replacement"} onChange={() => onChange("subject_replacement")} disabled={disabled} />
        <span><strong>개체 교체</strong><small>원본 영상은 유지하고 캐릭터·제품 외형 교체</small></span>
      </label>
      <label>
        <input type="radio" name="reference-intent" checked={value === "reference_generation"} onChange={() => onChange("reference_generation")} disabled={disabled} />
        <span><strong>장면 재구성</strong><small>동작·카메라·리듬을 참고해 새 장면 생성</small></span>
      </label>
    </fieldset>
  );
}

function QueueItem({ job, selected, onSelect }: { job: Job; selected: boolean; onSelect: () => void }) {
  const realismActive = job.realism_upgrade.status === "requested" || job.realism_upgrade.status === "processing";
  return (
    <button className={`queue-item ${selected ? "selected" : ""}`} onClick={onSelect} aria-current={selected ? "true" : undefined}>
      <span className={`thumb ${job.reference_kind === "video" ? "video-thumb" : ""}`}>
        {job.reference_kind === "video"
          ? <span className="video-thumb-placeholder" aria-hidden="true"><FileVideo2 size={18} /></span>
          : <img src={job.reference_url} alt="" loading="lazy" />}
      </span>
      <span className="queue-copy">
        <span className="queue-name-row">
          {job.shot_index != null && <small>샷 {job.shot_index}</small>}
          <strong>{job.name}</strong>
        </span>
        <span className="queue-meta"><span className="queue-state"><StatusBadge status={job.status} provider={job.execution_provider} /><JobTimingLabel job={job} />{realismActive && <span className="realism-queue-state"><Sparkles size={11} />현실감 개선</span>}</span><time>{formatTime(job.updated_at)}</time></span>
        {ACTIVE_STATUSES.has(job.status) && <span className="mini-progress"><i style={{ width: `${Math.max(5, job.progress)}%` }} /></span>}
      </span>
    </button>
  );
}

export type QueueListEntry =
  | { kind: "job"; job: Job }
  | { kind: "sequence"; id: string; title: string; jobs: Job[] };

export function groupQueueJobs(jobs: Job[]): QueueListEntry[] {
  const entries: QueueListEntry[] = [];
  const sequences = new Map<string, Extract<QueueListEntry, { kind: "sequence" }>>();
  for (const job of jobs) {
    if (!job.production_group_id) {
      entries.push({ kind: "job", job });
      continue;
    }
    const existing = sequences.get(job.production_group_id);
    if (existing) {
      existing.jobs.push(job);
      continue;
    }
    const sequence = {
      kind: "sequence" as const,
      id: job.production_group_id,
      title: job.production_group?.title?.trim() || job.name.replace(/-(?:cut|shot)-?\d+$/i, ""),
      jobs: [job],
    };
    sequences.set(sequence.id, sequence);
    entries.push(sequence);
  }
  for (const entry of entries) {
    if (entry.kind === "sequence") {
      entry.jobs.sort((left, right) => (left.shot_index ?? 0) - (right.shot_index ?? 0));
    }
  }
  return entries;
}

function QueueSequence({
  entry,
  selectedId,
  onSelect,
}: {
  entry: Extract<QueueListEntry, { kind: "sequence" }>;
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const expected = entry.jobs[0]?.production_group?.shot_count || entry.jobs[0]?.shot_count || entry.jobs.length;
  const completed = entry.jobs.filter((job) => job.status === "completed").length;
  const failed = entry.jobs.filter((job) => job.status === "failed" || job.status === "interrupted").length;
  const active = entry.jobs.some((job) => ACTIVE_STATUSES.has(job.status));
  const state = failed ? `${failed}개 확인 필요` : active ? "진행 중" : completed === expected ? "완료" : `${completed}/${expected} 완료`;
  return (
    <section className="queue-sequence" aria-labelledby={`sequence-${entry.id}`}>
      <header className="queue-sequence-header">
        <ListVideo size={16} aria-hidden="true" />
        <span>
          <strong id={`sequence-${entry.id}`}>{entry.title}</strong>
          <small>연속 샷 · {expected}컷</small>
        </span>
        <em className={failed ? "problem" : active ? "active" : completed === expected ? "complete" : ""}>{state}</em>
      </header>
      <div className="queue-sequence-jobs">
        {entry.jobs.map((job) => (
          <QueueItem
            key={job.id}
            job={job}
            selected={job.id === selectedId}
            onSelect={() => onSelect(job.id)}
          />
        ))}
      </div>
    </section>
  );
}

interface EditorProps {
  job: Job;
  socialAccounts: SocialAccounts | null;
  onManageSocialAccounts: () => void;
  onChanged: () => Promise<void>;
  onCloned: (job: Job) => Promise<void>;
  onBack: () => void;
  announce: (message: string) => void;
}

function JobEditor({ job, socialAccounts, onManageSocialAccounts, onChanged, onCloned, onBack, announce }: EditorProps) {
  const [draft, setDraft] = useState<JobDraft>(() => draftFromJob(job));
  const [results, setResults] = useState<JobResult[]>([]);
  const [selectedResultId, setSelectedResultId] = useState<string | null>(null);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [rewriteSubmitting, setRewriteSubmitting] = useState(false);
  const [promptBeforeRewrite, setPromptBeforeRewrite] = useState<string | null>(null);
  const [promptTranslation, setPromptTranslation] = useState("");
  const [translationState, setTranslationState] = useState<"loading" | "ready" | "error">("loading");
  const [translationRetry, setTranslationRetry] = useState(0);
  const [advanced, setAdvanced] = useState(false);
  const [targetDuration, setTargetDuration] = useState(30);
  const [cutMode, setCutMode] = useState<"independent" | "previous_result_last_frame">("independent");
  const [continuityBundle, setContinuityBundle] = useState<ContinuityBundle | null>(null);
  const [error, setError] = useState("");
  const [deleteArmed, setDeleteArmed] = useState(false);
  const replaceInput = useRef<HTMLInputElement>(null);
  const auxiliaryInput = useRef<HTMLInputElement>(null);
  const promptTextarea = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setDraft(draftFromJob(job));
    setCutMode(job.status === "completed" ? "previous_result_last_frame" : "independent");
    setContinuityBundle(null);
    setError("");
    setDeleteArmed(false);
    setPromptBeforeRewrite(null);
  }, [job.id]);

  useEffect(() => {
    const controller = new AbortController();
    setPromptTranslation("");
    setTranslationState("loading");
    let timer: number | undefined;
    const loadTranslation = async () => {
      try {
        const result = await api.translatePrompt(job.prompt, controller.signal);
        if (controller.signal.aborted) return;
        if (result.status === "completed" && result.translation) {
          setPromptTranslation(result.translation);
          setTranslationState("ready");
          return;
        }
        timer = window.setTimeout(() => void loadTranslation(), 5_000);
      } catch (reason) {
        if (controller.signal.aborted) return;
        setTranslationState("error");
        setPromptTranslation(reason instanceof Error ? reason.message : "한국어 해석을 불러오지 못했습니다.");
      }
    };
    void loadTranslation();
    return () => {
      controller.abort();
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [job.id, job.prompt, translationRetry]);

  useEffect(() => {
    if (job.realism_upgrade.status !== "applied" || job.realism_upgrade.output_prompt !== job.prompt) return;
    setDraft((current) => current.prompt === job.realism_upgrade.input_prompt
      ? { ...current, prompt: job.prompt }
      : current);
  }, [job.prompt, job.realism_upgrade.input_prompt, job.realism_upgrade.output_prompt, job.realism_upgrade.status]);

  useEffect(() => {
    let cancelled = false;
    setResultsLoading(true);
    api.jobResults(job.id).then(({ results: nextResults }) => {
      if (cancelled) return;
      setResults(nextResults);
      setSelectedResultId((current) => (
        current && nextResults.some((result) => result.id === current)
          ? current
          : nextResults.find((result) => result.available)?.id ?? nextResults[0]?.id ?? null
      ));
    }).catch((reason) => {
      if (!cancelled) setError(reason instanceof Error ? reason.message : "영상 히스토리를 불러오지 못했습니다.");
    }).finally(() => {
      if (!cancelled) setResultsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [job.id, job.completed_at, job.result_url, job.updated_at]);

  useEffect(() => {
    if (!deleteArmed) return;
    const timer = window.setTimeout(() => setDeleteArmed(false), 5000);
    return () => window.clearTimeout(timer);
  }, [deleteArmed]);

  const editable = EDITABLE_STATUSES.has(job.status);
  const dirty = JSON.stringify(draft) !== JSON.stringify(draftFromJob(job));
  const promptDirty = draft.prompt !== job.prompt;
  const rewriteActive = job.prompt_rewrite.status === "requested" || job.prompt_rewrite.status === "processing";
  const realismActive = job.realism_upgrade.status === "requested" || job.realism_upgrade.status === "processing";
  const realismEligible = job.reference_kind === "image" && (editable || job.status === "queued");
  const hasUnsupportedPrompt = draft.prompt.includes(UNSUPPORTED_PROMPT_MARKER);
  const suggestionNeedsReview = Boolean(
    job.prompt_rewrite.output_prompt?.includes(UNSUPPORTED_PROMPT_MARKER),
  );
  const selectedResult = results.find((result) => result.id === selectedResultId) || results[0] || null;
  function changeProfile(profile: string) {
    setDraft((current) => ({
      ...current,
      profile,
      ...(job.reference_kind === "video" && current.reference_intent === "subject_replacement"
        ? {}
        : PROFILE_DEFAULTS[profile]),
    }));
  }

  async function act(action: () => Promise<unknown>, success: string) {
    setBusy(true);
    setError("");
    try {
      await action();
      await onChanged();
      announce(success);
    } catch (reason) {
      const message = reason instanceof Error ? reason.message : "요청을 처리하지 못했습니다.";
      setError(message);
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    await act(() => api.updateJob(job.id, draft), "변경 내용을 저장했습니다.");
  }

  async function ready() {
    setBusy(true);
    setError("");
    try {
      if (dirty) await api.updateJob(job.id, draft);
      const prepared = await api.markReady(job.id);
      await onChanged();
      announce(prepared.status === "queued"
        ? "현재 생성 뒤 이어질 다음 배치 대기열에 바로 추가했습니다."
        : "작업을 실행 준비 목록에 넣었습니다.");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "준비 상태로 바꾸지 못했습니다.");
    } finally {
      setBusy(false);
    }
  }

  async function reopen() {
    await act(
      () => api.reopenJob(job.id),
      "기존 영상은 히스토리에 보관했습니다. 프롬프트를 수정해 새 버전을 만드세요.",
    );
  }

  async function cloneReference() {
    setBusy(true);
    setError("");
    try {
      const cloned = await api.cloneJob(job.id);
      await onCloned(cloned);
      announce("같은 레퍼런스로 새 초안을 만들었습니다.");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "새 작업을 만들지 못했습니다.");
    } finally {
      setBusy(false);
    }
  }

  async function replaceMedia(file?: File) {
    if (!file) return;
    let duration: number | null = null;
    let metadata: VideoMetadata | null = null;
    if (file.type.startsWith("video/")) {
      try {
        metadata = await probeVideoMetadata(file);
        duration = metadata.duration;
        if (duration < 2 || duration > 15) {
          setError(`레퍼런스 영상은 2–15초여야 합니다. 선택한 영상은 ${formatDuration(duration)}입니다.`);
          return;
        }
      } catch (reason) {
        setError(reason instanceof Error ? reason.message : "영상 정보를 확인하지 못했습니다.");
        return;
      }
    }
    await act(
      () => api.replaceReference(
        job.id,
        file,
        duration,
        metadata ? {
          referenceWidth: metadata.width,
          referenceHeight: metadata.height,
          ...ref2vaCanvasForSource(metadata.width, metadata.height),
        } : null,
      ),
      file.type.startsWith("video/") ? "레퍼런스 영상을 바꿨습니다." : "레퍼런스 이미지를 바꿨습니다.",
    );
    if (replaceInput.current) replaceInput.current.value = "";
  }

  async function addAuxiliaryImages(files?: FileList | null) {
    if (!files?.length) return;
    try {
      const selected = checkedReferenceImages(
        Array.from(files),
        MAX_REFERENCE_IMAGES - job.reference_images.length,
      );
      await act(
        () => api.addReferenceImages(job.id, selected),
        `보조 이미지 ${selected.length}장을 추가했습니다.`,
      );
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "보조 이미지를 추가하지 못했습니다.");
    } finally {
      if (auxiliaryInput.current) auxiliaryInput.current.value = "";
    }
  }

  async function removeAuxiliaryImage(imageId: string, tag: string) {
    await act(
      () => api.deleteReferenceImage(job.id, imageId),
      `${tag} 보조 이미지를 삭제했습니다.`,
    );
  }

  async function remove() {
    if (!deleteArmed) {
      setDeleteArmed(true);
      return;
    }
    await act(
      () => api.deleteJob(job.id),
      job.status === "queued" ? "대기열에서 작업을 삭제했습니다." : "작업을 삭제했습니다.",
    );
  }

  async function requestRewrite() {
    if (!draft.prompt.trim()) {
      setError("먼저 만들고 싶은 영상을 편하게 적어주세요.");
      return;
    }
    setRewriteSubmitting(true);
    setError("");
    try {
      await api.rewritePrompt(job.id, draft.prompt);
      await onChanged();
      announce("H3 프롬프트 변환을 완료했습니다.");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "프롬프트 다듬기를 요청하지 못했습니다.");
      await onChanged();
    } finally {
      setRewriteSubmitting(false);
    }
  }

  async function requestRealism() {
    setBusy(true);
    setError("");
    try {
      if (dirty && editable) await api.updateJob(job.id, draft);
      await api.upgradeRealism(job.id);
      await onChanged();
      announce("첫 프레임과 프롬프트의 현실감 분석을 요청했습니다. 기존 대기 순서는 유지됩니다.");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "현실감 개선을 요청하지 못했습니다.");
      await onChanged();
    } finally {
      setBusy(false);
    }
  }

  function applyRewrite() {
    const suggestion = job.prompt_rewrite.output_prompt;
    if (!suggestion) return;
    setPromptBeforeRewrite(draft.prompt);
    setDraft((current) => ({ ...current, prompt: suggestion }));
    announce("추천 프롬프트를 적용했습니다. 저장 전까지 되돌릴 수 있습니다.");
  }

  function undoRewrite() {
    if (promptBeforeRewrite === null) return;
    setDraft((current) => ({ ...current, prompt: promptBeforeRewrite }));
    setPromptBeforeRewrite(null);
    announce("적용 전 프롬프트로 되돌렸습니다.");
  }

  async function retryPostprocess(result: JobResult) {
    setBusy(true);
    setError("");
    try {
      const updated = await api.retryPostprocess(job.id, result.id);
      setResults((current) => current.map((item) => item.id === updated.id ? updated : item));
      announce("이 영상의 후처리 분석을 다시 요청했습니다.");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "후처리 분석을 다시 요청하지 못했습니다.");
    } finally {
      setBusy(false);
    }
  }

  async function requestMotionFix(result: JobResult, speedFactor: 1.1 | 1.2 | 1.35) {
    setBusy(true);
    setError("");
    try {
      const updated = await api.requestMotionFix(job.id, result.id, speedFactor);
      setResults((current) => current.map((item) => item.id === updated.id ? updated : item));
      announce(`${speedFactor}× 속도 보정을 요청했습니다. 원본은 그대로 유지됩니다.`);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "속도 보정을 요청하지 못했습니다.");
    } finally {
      setBusy(false);
    }
  }

  async function copyAdvice(value: string, message: string) {
    try {
      await navigator.clipboard.writeText(value);
      announce(message);
    } catch {
      setError("클립보드에 복사하지 못했습니다.");
    }
  }

  async function createCutPlan() {
    setBusy(true);
    setError("");
    try {
      if (dirty) await api.updateJob(job.id, draft);
      const plan = await api.createCutPlan(job.id, targetDuration);
      await onChanged();
      announce(`${targetDuration}초 완성본을 위한 ${plan.shot_count}개 컷 초안을 만들었습니다. 각 컷의 동작을 조정해 주세요.`);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "멀티컷 구성을 만들지 못했습니다.");
    } finally {
      setBusy(false);
    }
  }

  async function loadContinuityBundle(file?: File) {
    if (!file) return;
    try {
      const value = parseContinuityBundle(await file.text());
      setContinuityBundle(value);
      setCutMode("previous_result_last_frame");
      setTargetDuration(value.shots.reduce((sum, shot) => sum + shot.duration_seconds, 0));
      setError("");
    } catch (reason) {
      setContinuityBundle(null);
      setError(reason instanceof Error ? reason.message : "연속 샷 번들을 읽지 못했습니다.");
    }
  }

  async function createAndQueueContinuityChain() {
    if (!continuityBundle) {
      setError("contents-maker의 queue-chain.json을 먼저 선택하세요.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      if (dirty && editable) await api.updateJob(job.id, draft);
      const plan = await api.createCutPlan(job.id, targetDuration, {
        title: continuityBundle.title || job.name,
        continuity_mode: "previous_result_last_frame",
        shots: continuityBundle.shots,
      });
      const batch = await api.queueProduction(plan.group_id);
      await onChanged();
      announce(`${plan.shot_count}개 연속 샷을 한 번에 등록했습니다. 앞 샷 마지막 프레임을 다음 샷으로 자동 전달합니다.`);
      void batch;
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "연속 샷 체인을 등록하지 못했습니다.");
    } finally {
      setBusy(false);
    }
  }

  const editorProfiles = visibleProfilesForJob(job.reference_kind, job.profile);
  const selectedProfile = editorProfiles.find((profile) => profile.value === draft.profile);

  return (
    <article className="editor" aria-labelledby="job-title">
      <header className="editor-header">
        <button className="icon-button mobile-back" onClick={onBack} aria-label="작업 목록으로"><ArrowLeft /></button>
        <div className="editor-title">
          <div className="title-line"><h1 id="job-title">{job.name}</h1><StatusBadge status={job.status} provider={job.execution_provider} /></div>
          <p>{job.stage || `${selectedProfile?.label || job.profile} · ${job.width}×${job.height}`}<JobTimingLabel job={job} detail separator /></p>
        </div>
        {dirty && editable && <span className="unsaved">저장 안 됨</span>}
      </header>

      <div className="editor-body">
        <section className="reference-pane" aria-labelledby="reference-title">
          <div className="section-heading"><h2 id="reference-title">{job.reference_kind === "video" ? "레퍼런스 자산" : "첫 프레임"}</h2><span>{job.reference_kind === "video" ? `Ref2VA · Video 1${job.reference_images.length ? ` + Picture ${job.reference_images.length}` : ""}` : "9:16 reference"}</span></div>
          <div className={`reference-frame ${job.reference_kind === "video" ? "is-video" : ""}`}>
            {job.reference_kind === "video"
              ? <video src={job.reference_url} controls playsInline preload="metadata">브라우저에서 레퍼런스 영상을 재생할 수 없습니다.</video>
              : <img src={job.reference_url} alt={`${job.name} 레퍼런스`} />}
          </div>
          {editable && (
            <>
              <input ref={replaceInput} className="visually-hidden" type="file" accept={`${IMAGE_ACCEPT},${VIDEO_ACCEPT}`} onChange={(event) => void replaceMedia(event.target.files?.[0])} />
              <button className="button subtle wide" onClick={() => replaceInput.current?.click()} disabled={busy}><Upload size={17} />레퍼런스 바꾸기</button>
            </>
          )}
          <div className="reference-facts">
            <span><FileVideo2 size={15} />{job.reference_kind === "video" ? `참조 ${formatDuration(job.reference_duration)}` : `출력 약 ${Math.round(job.frames / 24)}초`}</span>
            <span>{job.width} × {job.height}</span>
            <span>{job.frames} frames</span>
          </div>
          {job.reference_kind === "video" && (
            <section className="editor-aux-references" aria-labelledby="editor-aux-title">
              <div className="aux-reference-heading">
                <div><strong id="editor-aux-title">보조 이미지</strong><small>제품·브랜드·디테일 참조</small></div>
                <span>{job.reference_images.length} / {MAX_REFERENCE_IMAGES}</span>
              </div>
              {editable && (
                <input
                  ref={auxiliaryInput}
                  className="visually-hidden"
                  type="file"
                  accept={IMAGE_ACCEPT}
                  multiple
                  onChange={(event) => void addAuxiliaryImages(event.target.files)}
                />
              )}
              <div className="aux-reference-grid">
                {job.reference_images.map((image, index) => (
                  <div className="aux-reference-card" key={image.id}>
                    <img src={image.url} alt={`${image.tag} ${image.name}`} loading="lazy" />
                    <span>{image.tag}</span>
                    {editable && (
                      <button
                        type="button"
                        className="aux-remove"
                        onClick={() => void removeAuxiliaryImage(image.id, image.tag)}
                        disabled={busy}
                        aria-label={`${image.tag} 삭제`}
                      ><X size={14} /></button>
                    )}
                  </div>
                ))}
                {editable && job.reference_images.length < MAX_REFERENCE_IMAGES && (
                  <button className="aux-add" type="button" onClick={() => auxiliaryInput.current?.click()} disabled={busy}>
                    <ImagePlus size={18} /><span>이미지 추가</span>
                  </button>
                )}
              </div>
              <p>표시 순서대로 프롬프트의 <code>&lt;Picture 1&gt;</code>부터 연결됩니다.</p>
            </section>
          )}
        </section>

        <section className="prompt-pane" aria-labelledby="prompt-title">
          <div className="section-heading"><h2 id="prompt-title">연출</h2><span>MiniMax H3</span></div>
          <label className="field">작업 이름<input value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} disabled={!editable || busy} /></label>
          {job.reference_kind === "video" && <ReferenceIntentSelector value={draft.reference_intent} onChange={(reference_intent) => setDraft((current) => ({
            ...current,
            reference_intent,
            ...(reference_intent === "subject_replacement" && job.reference_width && job.reference_height
              ? ref2vaCanvasForSource(job.reference_width, job.reference_height)
              : {}),
          }))} disabled={!editable || busy} />}
          <PromptSyntaxEditor
            label="영상 프롬프트"
            value={draft.prompt}
            disabled={!editable || busy}
            textareaRef={promptTextarea}
            translation={promptTranslation}
            translationState={translationState}
            translationStale={promptDirty}
            onChange={(prompt) => setDraft((current) => ({ ...current, prompt }))}
            onRetryTranslation={() => setTranslationRetry((value) => value + 1)}
            onRequestEdit={job.status === "completed" && !busy ? reopen : undefined}
          />
          <p className="field-hint">{job.reference_kind === "video" ? draft.reference_intent === "subject_replacement" ? `Video 1의 원본 역할·컷·타이밍을 보존하고 ${job.reference_images.length ? job.reference_images.map((image) => image.tag).join(", ") : "Picture N"} 외형을 attribute_transfer로 연결하세요.` : (job.reference_images.length ? `<Video 1>의 동작과 ${job.reference_images.map((image) => image.tag).join(", ")}의 제품·브랜드 역할을 각각 명시하세요.` : "<Video 1>에서 가져올 인물·동작·카메라·스타일을 명시하고, 목표 영상의 장면과 사운드를 적으세요.") : "주체와 배경을 먼저 고정한 뒤, 시간 순서대로 동작·카메라·조명·사운드를 적으면 안정적입니다."}</p>
          {hasUnsupportedPrompt && (
            <div className="unsupported-warning" role="alert">
              <AlertTriangle size={17} />
              <div>
                <strong>직접 확인이 필요한 부분이 남아 있습니다</strong>
                <p>{UNSUPPORTED_PROMPT_MARKER} 표시를 안전한 내용으로 수정해야 실행 준비로 바꿀 수 있습니다.</p>
              </div>
            </div>
          )}

          {job.reference_kind === "image" && (realismEligible || job.realism_upgrade.status) && (
            <div className="prompt-assistant realism-assistant" aria-live="polite">
              <div className="prompt-assistant-intro">
                <span className="assistant-mark realism-mark" aria-hidden="true"><Sparkles size={16} /></span>
                <div>
                  <strong>현실감 개선</strong>
                  <p>Codex가 첫 프레임과 현재 H3 프롬프트를 함께 보고 동작의 인과관계·물리 접촉·카메라·빛을 정리합니다. 대기 상태와 순서는 그대로 유지됩니다.</p>
                </div>
                {realismEligible && (
                  <button
                    className="button subtle compact"
                    onClick={requestRealism}
                    disabled={busy || realismActive || (job.realism_upgrade.status === "applied" && job.realism_upgrade.output_prompt === job.prompt)}
                  >
                    {realismActive ? <LoaderCircle className="spin" size={16} /> : <Sparkles size={16} />}
                    {job.realism_upgrade.status === "requested"
                      ? "분석 대기 중"
                      : job.realism_upgrade.status === "processing"
                        ? "현실감 분석 중"
                        : job.realism_upgrade.status === "applied" && job.realism_upgrade.output_prompt === job.prompt
                          ? "개선 적용됨"
                          : "현실감 높이기"}
                  </button>
                )}
              </div>
              {job.realism_upgrade.status === "requested" && (
                <p className="rewrite-status"><Clock3 size={15} />Mac 브리지의 Codex 분석을 기다리고 있습니다. 이 작업은 그동안 영상 워커가 가져가지 않습니다.</p>
              )}
              {job.realism_upgrade.status === "processing" && (
                <p className="rewrite-status active"><LoaderCircle className="spin" size={15} />첫 프레임을 기준으로 움직임·접촉·렌즈·조명을 다시 설계하고 있습니다.</p>
              )}
              {job.realism_upgrade.status === "failed" && (
                <div className="rewrite-error" role="alert"><AlertTriangle size={16} /><span>{job.realism_upgrade.error || "현실감 개선을 적용하지 못했습니다. 기존 프롬프트는 유지되었습니다."}</span></div>
              )}
              {job.realism_upgrade.status === "applied" && (
                <div className="realism-result">
                  <div className="realism-score">
                    <span>예상 현실감</span>
                    <strong>{job.realism_upgrade.score_before ?? "—"} <i aria-hidden="true">→</i> {job.realism_upgrade.score_after ?? "—"}</strong>
                  </div>
                  {job.realism_upgrade.changes.length > 0 && (
                    <ul className="rewrite-notes">
                      {job.realism_upgrade.changes.map((change, index) => <li key={`${index}-${change}`}>{change}</li>)}
                    </ul>
                  )}
                  {job.realism_upgrade.warnings.length > 0 && (
                    <div className="realism-warnings"><AlertTriangle size={15} /><div>{job.realism_upgrade.warnings.map((warning, index) => <p key={`${index}-${warning}`}>{warning}</p>)}</div></div>
                  )}
                </div>
              )}
            </div>
          )}

          {editable && job.reference_kind === "image" && (
            <div className="prompt-assistant" aria-live="polite">
              <div className="prompt-assistant-intro">
                <span className="assistant-mark" aria-hidden="true"><Sparkles size={16} /></span>
                <div>
                  <strong>H3 프롬프트 다듬기</strong>
                  <p>원문은 그대로 두고 아이디어를 H3 I2VA 형식으로 정리합니다. 레퍼런스 이미지는 외부로 전송하지 않습니다.</p>
                </div>
                <button
                  className="button subtle compact"
                  onClick={requestRewrite}
                  disabled={busy || rewriteSubmitting || rewriteActive}
                >
                  {rewriteSubmitting || rewriteActive
                    ? <LoaderCircle className="spin" size={16} />
                    : <Sparkles size={16} />}
                  {job.prompt_rewrite.status === "requested"
                    ? "변환 대기 중"
                    : job.prompt_rewrite.status === "processing"
                      ? "프롬프트 변환 중"
                      : rewriteSubmitting
                        ? "프롬프트 변환 중"
                        : "H3 형식으로 다듬기"}
                </button>
              </div>

              {job.prompt_rewrite.status === "requested" && (
                <p className="rewrite-status"><Clock3 size={15} />이전 변환 요청을 처리 대기 중입니다.</p>
              )}
              {job.prompt_rewrite.status === "processing" && (
                <p className="rewrite-status active"><LoaderCircle className="spin" size={15} />프롬프트 도우미가 행동·카메라·사운드 구조를 정리하고 있습니다.</p>
              )}
              {job.prompt_rewrite.status === "failed" && (
                <div className="rewrite-error" role="alert">
                  <AlertTriangle size={16} />
                  <span>{job.prompt_rewrite.error || "프롬프트를 다듬지 못했습니다. 다시 요청해 주세요."}</span>
                </div>
              )}
              {job.prompt_rewrite.status === "completed" && job.prompt_rewrite.output_prompt && (
                <div className="rewrite-result">
                  <div className="rewrite-result-heading">
                    <div className={suggestionNeedsReview ? "needs-review" : ""}>
                      {suggestionNeedsReview ? <AlertTriangle size={16} /> : <Check size={16} />}
                      <strong>{suggestionNeedsReview ? "검토가 필요한 변환본" : "추천 변환본"}</strong>
                    </div>
                    <span>적용 전에는 원문이 바뀌지 않습니다</span>
                  </div>
                  <textarea
                    className="rewrite-preview"
                    value={job.prompt_rewrite.output_prompt}
                    readOnly
                    rows={10}
                    aria-label="H3 추천 프롬프트"
                  />
                  {job.prompt_rewrite.notes.length > 0 && (
                    <ul className="rewrite-notes">
                      {job.prompt_rewrite.notes.map((note, index) => <li key={`${index}-${note}`}>{note}</li>)}
                    </ul>
                  )}
                  <div className="rewrite-actions">
                    <button className="button primary compact" onClick={applyRewrite} disabled={draft.prompt === job.prompt_rewrite.output_prompt}><Check size={16} />변환본 적용</button>
                    {promptBeforeRewrite !== null && <button className="button subtle compact" onClick={undoRewrite}><RotateCcw size={16} />원문으로 되돌리기</button>}
                    <button className="button subtle compact" onClick={requestRewrite} disabled={rewriteSubmitting}><RefreshCw size={16} />다시 생성</button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="profile-grid" role="radiogroup" aria-label="생성 방식">
            {editorProfiles.map((profile) => (
              <button
                key={profile.value}
                role="radio"
                aria-checked={draft.profile === profile.value}
                className={draft.profile === profile.value ? "active" : ""}
                onClick={() => changeProfile(profile.value)}
                disabled={!editable || busy}
              >
                <span className="profile-card-title">
                  <strong>{profile.label}</strong>
                  {job.profile === profile.value && <small>저장됨</small>}
                </span>
                <span>{profile.detail}</span>
                <span className="profile-estimate">{profile.eta}{profile.quality ? ` · ${profile.quality}` : ""}</span>
                <span className="profile-use">{profile.bestFor}</span>
              </button>
            ))}
          </div>

          <button className="advanced-toggle" onClick={() => setAdvanced((value) => !value)} aria-expanded={advanced}>
            상세 설정 <ChevronDown className={advanced ? "open" : ""} size={17} />
          </button>
          {advanced && (
            <div className="advanced-grid">
              <label>너비<input type="number" step="32" min="256" max="1344" value={draft.width} disabled={!editable || busy} onChange={(event) => setDraft({ ...draft, width: Number(event.target.value) })} /></label>
              <label>높이<input type="number" step="32" min="256" max="1344" value={draft.height} disabled={!editable || busy} onChange={(event) => setDraft({ ...draft, height: Number(event.target.value) })} /></label>
              <label>프레임<input type="number" step="17" min="124" max="362" value={draft.frames} disabled={!editable || busy} onChange={(event) => setDraft({ ...draft, frames: Number(event.target.value) })} /></label>
              <label>시드<input type="number" min="0" max="4294967295" value={draft.seed} disabled={!editable || busy} onChange={(event) => setDraft({ ...draft, seed: Number(event.target.value) })} /></label>
            </div>
          )}

          {(editable || job.status === "completed") && (
            <section className="cut-planner" aria-labelledby="cut-planner-title">
              <div>
                <ListPlus size={17} />
                <div><strong id="cut-planner-title">여러 샷 제작</strong><p>독립 컷으로 나누거나, 앞 샷 마지막 프레임을 다음 샷 시작점으로 자동 연결합니다.</p></div>
              </div>
              {job.production_group_id ? (
                <div className="cut-plan-status">
                  <span className="cut-plan-active">컷 {job.shot_index}/{job.shot_count} · 목표 {job.target_duration_seconds}초</span>
                  {job.production_group?.continuity_mode === "previous_result_last_frame" && <span><Check size={14} />이전 샷 마지막 프레임 자동 연결</span>}
                  {job.production_group?.status === "requested" && <span><Clock3 size={14} />모든 컷 완료 · 자동 결합 대기</span>}
                  {job.production_group?.status === "processing" && <span><LoaderCircle className="spin" size={14} />음성을 유지하며 자동 결합 중</span>}
                  {job.production_group?.status === "completed" && job.production_group.result_url && <a className="button primary compact" href={job.production_group.result_url}><Download size={14} />{job.production_group.target_duration_seconds}초 결합본 다운로드 <small>{formatBytes(job.production_group.result_size)}</small></a>}
                  {job.production_group?.status === "failed" && <span className="cut-plan-error"><AlertTriangle size={14} />{job.production_group.error || "자동 결합에 실패했습니다. Mac의 개별 결과는 유지됩니다."}</span>}
                </div>
              ) : (
                <div className="cut-planner-modes">
                  <div className="cut-mode-switch" role="tablist" aria-label="여러 샷 방식">
                    {editable && <button role="tab" aria-selected={cutMode === "independent"} className={cutMode === "independent" ? "active" : ""} onClick={() => setCutMode("independent")}>독립 멀티컷</button>}
                    <button role="tab" aria-selected={cutMode === "previous_result_last_frame"} className={cutMode === "previous_result_last_frame" ? "active" : ""} onClick={() => setCutMode("previous_result_last_frame")}>연속 샷 체인</button>
                  </div>
                  {cutMode === "independent" && editable ? (
                    <div className="cut-planner-controls">
                      <label>완성 길이<input type="number" min="16" max="90" step="1" value={targetDuration} onChange={(event) => setTargetDuration(Number(event.target.value))} /></label>
                      <span>{multiCutCount(targetDuration)}개 컷</span>
                      <button className="button subtle compact" onClick={() => void createCutPlan()} disabled={busy}><ListPlus size={15} />컷 구성 만들기</button>
                    </div>
                  ) : (
                    <div className="continuity-chain-controls">
                      <p>현재 작업을 샷 1로 삼습니다. 생성된 마지막 프레임을 다음 샷의 <code>&lt;Picture 1&gt;</code>로 넘기고, 모든 샷을 순서대로 생성한 뒤 자동 결합합니다.</p>
                      <label className="button subtle compact"><Upload size={15} />queue-chain.json 선택<input className="visually-hidden" type="file" accept="application/json,.json" onChange={(event) => void loadContinuityBundle(event.target.files?.[0])} /></label>
                      {continuityBundle && <span className="chain-bundle-summary"><Check size={14} />{continuityBundle.title || "연속 샷"} · {continuityBundle.shots.length}샷 · {targetDuration}초</span>}
                      <button className="button primary compact" onClick={() => void createAndQueueContinuityChain()} disabled={busy || !continuityBundle}><ListPlus size={15} />전체 체인 대기열 등록</button>
                    </div>
                  )}
                </div>
              )}
            </section>
          )}

          {job.error && <div className="error-panel" role="alert"><AlertTriangle size={18} /><div><strong>{job.status === "interrupted" ? "생성을 중단했습니다" : "작업을 마치지 못했습니다"}</strong><p>{readableJobError(job.error, job.status)}</p></div></div>}
          {error && <p className="form-error" role="alert">{error}</p>}

          <div className="editor-actions">
            {editable && <button className="button subtle" onClick={save} disabled={busy || !dirty}><Save size={17} />저장</button>}
            {editable && <button className="button primary" onClick={ready} disabled={busy || rewriteActive || realismActive || hasUnsupportedPrompt}>{job.status === "failed" || job.status === "interrupted" ? <RotateCcw size={17} /> : <Check size={17} />}{job.status === "failed" || job.status === "interrupted" ? "다시 준비" : job.status === "ready" ? "준비 상태 유지" : "실행 준비"}</button>}
            {job.status === "completed" && <button className="button subtle" onClick={reopen} disabled={busy}><PencilLine size={17} />프롬프트 수정 후 새 버전</button>}
            {!ACTIVE_STATUSES.has(job.status) && <button className="button subtle" onClick={cloneReference} disabled={busy || rewriteActive || realismActive}>{job.reference_kind === "video" ? <FileVideo2 size={17} /> : <ImagePlus size={17} />}같은 레퍼런스로 새 작업</button>}
            {job.result_url && (!results[0] || results[0].available) && <a className="button primary" href={job.result_url}><Download size={17} />최신 영상 다운로드 <small>{formatBytes(job.result_size)}</small></a>}
            {(!ACTIVE_STATUSES.has(job.status) || job.status === "queued") && <button className={`button danger-link ${deleteArmed ? "armed" : ""}`} onClick={remove} disabled={busy || rewriteActive || realismActive} aria-label={deleteArmed ? "정말로 이 작업 삭제" : job.status === "queued" ? "대기 작업 삭제" : "작업 삭제"}><Trash2 size={17} />{deleteArmed ? "정말 삭제" : job.status === "queued" ? "대기 작업 삭제" : "삭제"}</button>}
          </div>

          {(resultsLoading || results.length > 0) && (
            <section className="result-library" aria-labelledby="result-library-title">
              <header className="result-library-heading">
                <div>
                  <History size={17} />
                  <div><h3 id="result-library-title">생성 히스토리</h3><p>이 작업에서 만든 영상을 바로 보거나 다시 다운로드할 수 있습니다.</p></div>
                </div>
                <span>{results.length}개 버전</span>
              </header>
              {resultsLoading && !results.length ? (
                <p className="result-loading"><LoaderCircle className="spin" size={16} />영상 기록 불러오는 중</p>
              ) : (
                <>
                <div className="result-library-grid">
                  <div className="result-viewer">
                    {selectedResult?.available ? (
                      <video
                        key={selectedResult.id}
                        controls
                        playsInline
                        preload="metadata"
                        poster={job.reference_kind === "image" ? job.reference_url : undefined}
                        src={selectedResult.video_url}
                      >
                        브라우저에서 영상을 재생할 수 없습니다.
                      </video>
                    ) : (
                      <div className="result-unavailable"><FileVideo2 size={24} /><strong>클라우드 보관 종료</strong><span>Mac에 저장된 결과는 그대로 남아 있습니다.</span></div>
                    )}
                    {selectedResult && (
                      <div className="result-viewer-meta">
                        <div><strong>{selectedResult.name}</strong><span>{formatTime(selectedResult.created_at)} · {formatBytes(selectedResult.size)} · H3 {formatElapsed(selectedResult.generation_seconds)} · 전체 {formatElapsed(selectedResult.total_seconds)}</span></div>
                        {selectedResult.available && <a className="button subtle compact" href={selectedResult.download_url}><Download size={15} />다운로드</a>}
                        <p title={selectedResult.prompt}>{selectedResult.prompt}</p>
                      </div>
                    )}
                    {selectedResult && (
                      <div className="postprocess-advice" aria-live="polite">
                        <div className="postprocess-heading">
                          <div><Sparkles size={16} /><strong>이 영상 후처리 제안</strong></div>
                          {selectedResult.postprocess.verdict && <span className={`verdict ${selectedResult.postprocess.verdict}`}>{selectedResult.postprocess.verdict === "publish" ? "바로 사용" : selectedResult.postprocess.verdict === "postprocess" ? "후처리 권장" : "재생성 권장"}</span>}
                        </div>
                        {(selectedResult.postprocess.status === "requested" || selectedResult.postprocess.status === "processing") && (
                          <p className="postprocess-wait"><LoaderCircle className="spin" size={15} />결과 프레임을 읽고 업스케일·보간·색보정·재생성 중 필요한 것만 고르고 있습니다.</p>
                        )}
                        {selectedResult.postprocess.status === "failed" && (
                          <div className="postprocess-error"><AlertTriangle size={15} /><span>{selectedResult.postprocess.error || "영상 분석에 실패했습니다."}</span><button className="button subtle compact" onClick={() => void retryPostprocess(selectedResult)} disabled={busy}><RefreshCw size={14} />다시 분석</button></div>
                        )}
                        {selectedResult.postprocess.status === "completed" && (
                          <>
                            {selectedResult.reference_audit && (
                              <div className={`reference-audit ${selectedResult.reference_audit.passed ? "passed" : "failed"}`}>
                                {selectedResult.reference_audit.passed ? <CheckCircle2 size={15} /> : <AlertTriangle size={15} />}
                                <div>
                                  <strong>{selectedResult.reference_audit.passed ? "원본 타이밍 일치" : selectedResult.reference_audit.direct_edit_required ? "개체 교체 감사 불합격" : "레퍼런스 타이밍 차이"}</strong>
                                  <span>원본 컷 {selectedResult.reference_audit.source_hard_cuts_seconds?.length ?? 0}개 · 결과 컷 {selectedResult.reference_audit.result_hard_cuts_seconds?.length ?? 0}개 · 길이 차이 {Math.abs(selectedResult.reference_audit.duration_delta_seconds ?? 0).toFixed(2)}초</span>
                                </div>
                              </div>
                            )}
                            <p className="postprocess-summary">{selectedResult.postprocess.summary}</p>
                            {selectedResult.postprocess.actions.length > 0 && <ol className="postprocess-actions">{selectedResult.postprocess.actions.map((action, index) => <li key={`${index}-${action}`}>{action}</li>)}</ol>}
                            {selectedResult.postprocess.prompt && (
                              <div className="postprocess-prompt">
                                <label>후처리 도구용 프롬프트<textarea value={selectedResult.postprocess.prompt} readOnly rows={5} /></label>
                                <button className="button subtle compact" onClick={() => void copyAdvice(selectedResult.postprocess.prompt || "", "후처리 프롬프트를 복사했습니다.")}><Copy size={14} />복사</button>
                              </div>
                            )}
                            {selectedResult.postprocess.rerender_prompt_appendix && (
                              <details className="rerender-appendix"><summary>H3 재생성 보정문</summary><p>{selectedResult.postprocess.rerender_prompt_appendix}</p><button className="button subtle compact" onClick={() => void copyAdvice(selectedResult.postprocess.rerender_prompt_appendix || "", "재생성 보정문을 복사했습니다.")}><Copy size={14} />복사</button></details>
                            )}
                          </>
                        )}
                        {selectedResult.motion_fix.status && (
                          <div className="motion-fix-status">
                            {(selectedResult.motion_fix.status === "requested" || selectedResult.motion_fix.status === "processing") && <p className="postprocess-wait"><LoaderCircle className="spin" size={15} />{selectedResult.motion_fix.factor}×로 영상·오디오 속도를 함께 보정 중입니다.</p>}
                            {selectedResult.motion_fix.status === "completed" && selectedResult.motion_fix.download_url && <a className="button primary compact" href={selectedResult.motion_fix.download_url}><Download size={14} />{selectedResult.motion_fix.factor}× 속도 보정본 <small>{formatBytes(selectedResult.motion_fix.size)}</small></a>}
                            {selectedResult.motion_fix.status === "failed" && <div className="postprocess-error"><AlertTriangle size={15} /><span>{selectedResult.motion_fix.error || "속도 보정에 실패했습니다."}</span></div>}
                          </div>
                        )}
                        {selectedResult.available && selectedResult.motion_fix.status !== "requested" && selectedResult.motion_fix.status !== "processing" && (
                          <details className="motion-fix-controls">
                            <summary>동작이 너무 느리면 속도 보정</summary>
                            <p>영상과 오디오를 함께 당깁니다. 원본은 유지되며 1.1×부터 확인하는 편이 안전합니다.</p>
                            <div>
                              {([1.1, 1.2, 1.35] as const).map((factor) => (
                                <button key={factor} className={`button compact ${selectedResult.postprocess.speed_factor === factor ? "primary" : "subtle"}`} onClick={() => void requestMotionFix(selectedResult, factor)} disabled={busy}>
                                  {selectedResult.postprocess.speed_factor === factor ? `추천 ${factor}×` : `${factor}×`}
                                </button>
                              ))}
                            </div>
                          </details>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="result-history" role="list" aria-label="생성된 영상 버전">
                    {results.map((result, index) => (
                      <button
                        key={result.id}
                        className={`result-version ${result.id === selectedResult?.id ? "selected" : ""}`}
                        onClick={() => setSelectedResultId(result.id)}
                        aria-pressed={result.id === selectedResult?.id}
                        role="listitem"
                      >
                        <span className="result-version-number">v{results.length - index}</span>
                        <span className="result-version-copy">
                          <strong>{index === 0 ? "최신 결과" : formatTime(result.created_at)}</strong>
                          <small>{result.execution_provider === "floyo" ? "Floyo (기존)" : result.execution_provider === "vast" ? "Vast 5090" : result.execution_provider === "windows" ? "Windows 3060 Ti" : result.execution_provider === "runpod" ? "RunPod (기존)" : "H3"} · H3 {formatElapsed(result.generation_seconds)} · 전체 {formatElapsed(result.total_seconds)}</small>
                        </span>
                        <span className={result.available ? "available" : "expired"}>{result.available ? formatBytes(result.size) : "보관 종료"}</span>
                      </button>
                    ))}
                  </div>
                </div>
                {selectedResult?.available && <SocialPublisher key={selectedResult.id} result={selectedResult} accounts={socialAccounts} onManageAccounts={onManageSocialAccounts} announce={announce} />}
                </>
              )}
            </section>
          )}

          {editable && <p className="reference-video-note"><Server size={15} />실행 준비 후 아래 대기열에 넣으세요. 사람이 <code>h3-up</code>을 누르면 Vast RTX 5090 한 대가 켜지고 I2V와 Ref2VA 작업을 순서대로 처리합니다.</p>}

          {job.result_expires_at && <p className="retention-note"><Clock3 size={15} />최신 클라우드 결과는 {formatTime(job.result_expires_at)}까지 보관됩니다. Mac에는 계속 남습니다.</p>}
        </section>
      </div>
    </article>
  );
}

type Filter = "all" | "ready" | "active" | "problem" | "completed";

export function dashboardPollInterval(hasLiveWork: boolean): number {
  return hasLiveWork ? 5_000 : 15_000;
}

export function App() {
  const [auth, setAuth] = useState<"checking" | "in" | "out">("checking");
  const [jobs, setJobs] = useState<Job[]>([]);
  const [status, setStatus] = useState<DashboardStatus | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [newOpen, setNewOpen] = useState(false);
  const [socialAccountsOpen, setSocialAccountsOpen] = useState(false);
  const [localControlOpen, setLocalControlOpen] = useState(false);
  const [executionMode, setExecutionMode] = useState<"vast" | "windows">(() =>
    window.localStorage.getItem("h3-execution-mode") === "windows" ? "windows" : "vast",
  );
  const [socialAccounts, setSocialAccounts] = useState<SocialAccounts | null>(null);
  const [busy, setBusy] = useState(false);
  const [realismScanBusy, setRealismScanBusy] = useState(false);
  const [loadError, setLoadError] = useState("");
  const { message, announce } = useMessage();

  const refresh = useCallback(async (quiet = false) => {
    try {
      const snapshot = await api.snapshot();
      setJobs(snapshot.jobs);
      setStatus(snapshot.status);
      setLoadError("");
      setSelectedId((current) => current && snapshot.jobs.some((job) => job.id === current)
        ? current
        : snapshot.jobs[0]?.id ?? null);
    } catch (reason) {
      if (reason instanceof ApiError && reason.status === 401) {
        setAuth("out");
        return;
      }
      if (!quiet) setLoadError(reason instanceof Error ? reason.message : "목록을 불러오지 못했습니다.");
    }
  }, []);

  const loadSocialAccounts = useCallback(async () => {
    try {
      setSocialAccounts(await api.socialAccounts());
    } catch (reason) {
      if (reason instanceof ApiError && reason.status === 401) return;
      setLoadError(reason instanceof Error ? reason.message : "게시 계정을 불러오지 못했습니다.");
    }
  }, []);

  useEffect(() => {
    api.session().then((session) => {
      setAuth(session.authenticated ? "in" : "out");
    }).catch(() => setAuth("out"));
  }, []);

  useEffect(() => {
    if (auth !== "in") return;
    void refresh();
    void loadSocialAccounts();
  }, [auth, loadSocialAccounts, refresh]);

  const hasLiveWork = Boolean(
    jobs.some((job) => ["preparing", "rendering", "uploading"].includes(job.status))
    || (status?.counts.realism_pending || 0) > 0
    || status?.batch?.status === "running",
  );

  useEffect(() => {
    if (auth !== "in") return;
    let timer: number | undefined;
    let stopped = false;
    const schedule = () => {
      if (stopped || document.visibilityState !== "visible") return;
      timer = window.setTimeout(async () => {
        await refresh(true);
        schedule();
      }, dashboardPollInterval(hasLiveWork));
    };
    const handleVisibility = () => {
      if (timer !== undefined) window.clearTimeout(timer);
      timer = undefined;
      if (document.visibilityState === "visible") {
        void refresh(true);
        schedule();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    schedule();
    return () => {
      stopped = true;
      if (timer !== undefined) window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [auth, hasLiveWork, refresh]);

  useEffect(() => {
    const url = new URL(window.location.href);
    const social = url.searchParams.get("social");
    if (!social) return;
    const socialDetail = url.searchParams.get("social_detail") || "";
    setSocialAccountsOpen(true);
    url.searchParams.delete("social");
    url.searchParams.delete("social_detail");
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
    if (social.endsWith("-connected")) {
      announce(`${social.startsWith("instagram") ? "Instagram" : "TikTok"} 계정을 연결했습니다.`);
      void loadSocialAccounts();
    } else if (social.endsWith("-cancelled")) {
      setLoadError("게시 계정 연결을 취소했습니다.");
    } else {
      setLoadError(socialDetail || "게시 계정을 연결하지 못했습니다. 앱 설정과 승인 권한을 확인해 주세요.");
    }
  }, [announce, loadSocialAccounts]);

  const filteredJobs = useMemo(() => jobs.filter((job) => {
    if (filter === "ready") return job.status === "ready";
    if (filter === "active") return ACTIVE_STATUSES.has(job.status);
    if (filter === "problem") return job.status === "failed" || job.status === "interrupted";
    if (filter === "completed") return job.status === "completed";
    return true;
  }), [jobs, filter]);

  const selected = jobs.find((job) => job.id === selectedId) || null;
  const batchJobs = status?.batch ? jobs.filter((job) => job.batch_id === status.batch?.id) : [];
  const batchProgress = batchJobs.length
    ? Math.round(batchJobs.reduce((sum, job) => sum + job.progress, 0) / batchJobs.length)
    : 0;
  const runtime = status?.runtime;
  const currentBatchJob = (
    (status?.batch?.current_job_id && jobs.find((job) => job.id === status.batch?.current_job_id))
    || batchJobs.find((job) => ["preparing", "rendering", "uploading"].includes(job.status))
    || null
  );
  const runtimeElapsed = runtime ? formatRuntimeElapsed(runtime.started_at) : "";
  const runtimeCost = runtime ? formatSessionCost(runtime.hourly_usd, runtime.started_at) : "";
  const gpuStatus = resolveGpuStatus(runtime || null, Boolean(status?.bridge.online), status?.counts.active || 0);
  const runtimeStatusLine = runtime
    ? `${gpuStatus.detail}${currentBatchJob && resolveJobTiming(currentBatchJob)?.kind === "live" ? " · 현재 작업 경과시간 표시 중" : ""} · ${runtimeElapsed} · ${runtimeCost}`
    : gpuStatus.detail;
  const windowsWorkerReady = Boolean(status?.windows_worker?.online && status.windows_worker.comfy_ready);
  const windowsCandidates = status?.counts.windows_candidates || 0;
  const windowsActiveJobs = jobs.filter((job) => job.execution_provider === "windows" && ACTIVE_STATUSES.has(job.status));
  const isWindowsCandidate = (job: Job) => (
    (job.status === "ready"
      && !["requested", "processing"].includes(job.prompt_rewrite.status || "")
      && !["requested", "processing"].includes(job.realism_upgrade.status || "")
      && !job.prompt.includes(UNSUPPORTED_PROMPT_MARKER))
    || (job.status === "queued" && job.execution_provider === "vast"
      && status?.batch?.status === "requested" && job.batch_id === status.batch.id)
  );
  const selectedWindowsJobs = selected && isWindowsCandidate(selected)
    ? selected.production_group_id && selected.production_group?.continuity_mode === "previous_result_last_frame"
      ? jobs.filter((job) => job.production_group_id === selected.production_group_id && isWindowsCandidate(job))
      : [selected]
    : [];
  function chooseExecutionMode(mode: "vast" | "windows") {
    setExecutionMode(mode);
    window.localStorage.setItem("h3-execution-mode", mode);
  }

  async function runBatch() {
    setBusy(true);
    setLoadError("");
    try {
      const batch = await api.runBatch(
        executionMode,
        executionMode === "windows" ? selectedWindowsJobs.map((job) => job.id) : undefined,
      );
      announce(executionMode === "windows"
        ? `${batch.added_count}개 선택 작업을 Windows 3060 Ti 큐로 보냈습니다. 워커가 순서대로 자동 실행합니다.`
        : `${batch.added_count}개 작업을 Vast 대기열에 넣었습니다. 현재 총 ${batch.job_count}개이며 GPU는 켜지지 않았습니다.`);
      await refresh();
    } catch (reason) {
      setLoadError(reason instanceof Error ? reason.message : "작업을 대기열에 넣지 못했습니다.");
    } finally {
      setBusy(false);
    }
  }

  async function upgradeWaitingRealism() {
    setRealismScanBusy(true);
    setLoadError("");
    try {
      const result = await api.upgradeWaitingRealism();
      announce(result.requested_count
        ? `${result.requested_count}개 대기 작업의 현실감 분석을 요청했습니다. 기존 대기 순서는 유지됩니다.`
        : "현재 현실감 개선이 필요한 안전한 대기 작업이 없습니다.");
      await refresh();
    } catch (reason) {
      setLoadError(reason instanceof Error ? reason.message : "대기열 현실감 점검을 시작하지 못했습니다.");
    } finally {
      setRealismScanBusy(false);
    }
  }

  async function logout() {
    await api.logout().catch(() => undefined);
    setAuth("out");
    setJobs([]);
  }

  if (auth === "checking") {
    return <main className="loading-screen"><LoaderCircle className="spin" /><span>제작 큐 확인 중</span></main>;
  }
  if (auth === "out") return <LoginScreen onLogin={() => setAuth("in")} />;

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="brand"><span className="brand-mark small"><Sparkles size={17} /></span><strong>H3 제작 큐</strong><span className="edition">PRIVATE</span></div>
        <div className="topbar-status">
          <div className="execution-mode-switch" role="group" aria-label="실행 GPU 선택">
            <button className={executionMode === "vast" ? "active" : ""} onClick={() => chooseExecutionMode("vast")} aria-pressed={executionMode === "vast"}>Vast 5090</button>
            <button className={executionMode === "windows" ? "active" : ""} onClick={() => chooseExecutionMode("windows")} aria-pressed={executionMode === "windows"}>3060 Ti</button>
          </div>
          {executionMode === "vast" ? (
          <details className={`gpu-runtime ${gpuStatus.tone}`}>
            <summary aria-label={`${gpuStatus.label}. ${runtimeStatusLine}`}>
              {gpuStatus.tone === "starting" ? <LoaderCircle className="spin" size={16} /> : gpuStatus.tone === "off" ? <CircleOff size={16} /> : gpuStatus.tone === "unknown" ? <AlertTriangle size={16} /> : <Cpu size={16} />}
              <span className="gpu-runtime-copy">
                <strong>{gpuStatus.label}</strong>
                <small>{gpuStatus.detail}{currentBatchJob && <JobTimingLabel job={currentBatchJob} separator />}{runtime && <> · {runtimeElapsed} · {runtimeCost}</>}</small>
              </span>
              <ChevronDown className="gpu-runtime-chevron" size={14} aria-hidden="true" />
            </summary>
            <div className="gpu-runtime-panel">
              <div className="gpu-runtime-panel-heading">
                <strong>{gpuStatus.label}</strong>
                <span>{gpuStatus.detail}</span>
              </div>
              <dl>
                <div><dt>GPU</dt><dd>{runtime ? `${runtime.gpu_label}${runtime.vram_gb ? ` · ${runtime.vram_gb}GB VRAM` : ""}` : "RTX 5090 · 32GB VRAM"}</dd></div>
                <div><dt>임시 디스크</dt><dd>{runtime?.disk_gb || 80}GB · 종료 시 삭제</dd></div>
                <div><dt>Vast 계약 총액</dt><dd>{runtime?.hourly_usd != null ? `$${runtime.hourly_usd.toFixed(3)}/h` : "≤ $0.650/h"} · 디스크 포함</dd></div>
                <div><dt>현재 세션</dt><dd>{runtime ? `${runtimeElapsed} · ${runtimeCost}` : "미실행 · $0.00"}</dd></div>
                {currentBatchJob && <div><dt>현재 작업</dt><dd className="runtime-current-job"><span>{currentBatchJob.name}</span><JobTimingLabel job={currentBatchJob} detail /></dd></div>}
                <div><dt>예상 전송료</dt><dd>≤ $0.25/세션 · 계약요금과 별도</dd></div>
              </dl>
              <p>호스트 필터: 다운로드 ≤ $0.003/GB · 업로드 ≤ $0.01/GB · 저장 ≤ $0.03/h. 과금은 h3-up 임대 성공 시 시작하고 h3-down에서 끝납니다.</p>
            </div>
          </details>
          ) : (
            <button className={`windows-runtime ${status?.windows_worker?.online && status.windows_worker.comfy_ready ? "ready" : "off"}`} onClick={() => setLocalControlOpen(true)}>
              {status?.windows_worker?.online && status.windows_worker.comfy_ready ? <Cpu size={16} /> : <CircleOff size={16} />}
              <span><strong>Windows 워커</strong><small>{status?.windows_worker?.online ? (status.windows_worker.comfy_ready ? `${status.windows_worker.state} · Comfy 준비됨` : "Comfy 준비 중") : "오프라인"}</small></span>
            </button>
          )}
          <button className="social-accounts-trigger" onClick={() => setSocialAccountsOpen(true)}>
            <Share2 size={16} /><span>게시 계정</span>
            {Boolean(socialAccounts?.connections.length) && <em>{socialAccounts?.connections.length}</em>}
          </button>
          <a className="agent-guide-link" href="/AGENTS.md" target="_blank" rel="noreferrer"><Bot size={16} /><span>AI 연결</span></a>
          <span className={`connection ${status?.bridge.online ? "online" : "offline"}`}>
            {status?.bridge.online ? <Server size={16} /> : <CircleOff size={16} />}
            Mac 브리지 {status?.bridge.online ? "연결됨" : "오프라인"}
          </span>
          <button className="icon-button" onClick={() => void refresh()} aria-label="새로고침"><RefreshCw size={18} /></button>
          <button className="icon-button" onClick={logout} aria-label="로그아웃"><LogOut size={18} /></button>
        </div>
      </header>

      <LocalH3ControlPlane open={localControlOpen} onClose={() => setLocalControlOpen(false)} />

      <div className="workspace">
        <aside className={`queue-panel ${selected ? "mobile-hidden" : ""}`} aria-label="영상 작업 목록">
          <div className="queue-toolbar">
            <div><p className="eyebrow">QUEUE</p><h2>작업 {jobs.length}</h2></div>
            <button className="button primary compact" onClick={() => setNewOpen(true)}><Plus size={17} />새 작업</button>
          </div>
          <div className="filters" aria-label="작업 필터">
            {([
              ["all", "전체"], ["ready", "준비"], ["active", "진행"], ["problem", "확인"], ["completed", "완료"],
            ] as [Filter, string][]).map(([value, label]) => <button key={value} className={filter === value ? "active" : ""} onClick={() => setFilter(value)}>{label}</button>)}
          </div>
          <div className="queue-list">
            {groupQueueJobs(filteredJobs).map((entry) => entry.kind === "sequence" ? (
              <QueueSequence key={entry.id} entry={entry} selectedId={selectedId} onSelect={setSelectedId} />
            ) : (
              <QueueItem key={entry.job.id} job={entry.job} selected={entry.job.id === selectedId} onSelect={() => setSelectedId(entry.job.id)} />
            ))}
            {!filteredJobs.length && (
              <div className="empty-queue"><PencilLine size={24} /><strong>{jobs.length ? "이 상태의 작업이 없습니다" : "첫 작업을 준비하세요"}</strong><p>GPU를 켜지 않고 레퍼런스 이미지·영상과 프롬프트부터 쌓아둘 수 있습니다.</p><button className="button subtle" onClick={() => setNewOpen(true)}><Plus size={17} />새 작업</button></div>
            )}
          </div>
        </aside>

        <main className={`main-panel ${selected ? "mobile-open" : ""}`}>
          {selected ? <JobEditor job={selected} socialAccounts={socialAccounts} onManageSocialAccounts={() => setSocialAccountsOpen(true)} onChanged={() => refresh()} onCloned={async (cloned) => {
            await refresh();
            setSelectedId(cloned.id);
          }} onBack={() => setSelectedId(null)} announce={announce} /> : (
            <div className="empty-editor"><ImagePlus size={30} /><h1>작업을 선택하세요</h1><p>왼쪽 목록에서 편집할 작업을 고르거나 새 작업을 만드세요.</p></div>
          )}
        </main>
      </div>

      <footer className="run-rail">
        <div className="rail-progress" aria-hidden="true"><span style={{ transform: `scaleX(${(executionMode === "windows" ? (windowsActiveJobs.length ? Math.max(2, Math.round(windowsActiveJobs.reduce((sum, job) => sum + job.progress, 0) / windowsActiveJobs.length)) : 0) : (status?.batch ? Math.max(2, batchProgress) : 0)) / 100})` }} /></div>
        <div className="rail-copy">
          {executionMode === "windows" ? (
            windowsActiveJobs.length ? (
              <><LoaderCircle className="spin" size={18} /><div><strong>Windows 3060 Ti 작업 중</strong><span>{windowsActiveJobs.length}개 진행·대기 중{selectedWindowsJobs.length ? ` · 선택한 ${selectedWindowsJobs.length}개를 추가할 수 있습니다.` : " · 다른 대기 작업을 선택할 수 있습니다."}</span></div></>
            ) : windowsWorkerReady ? (
              <><Cpu size={18} /><div><strong>Windows 3060 Ti 준비됨</strong><span>{selectedWindowsJobs.length ? `선택한 ${selectedWindowsJobs.length}개를 실행합니다${selectedWindowsJobs.length > 1 ? " · 연속 샷은 한 묶음으로 처리됩니다." : "."}` : windowsCandidates ? `대기 목록에서 실행할 작업을 선택하세요 · 전체 ${windowsCandidates}개` : "실행할 준비 작업이 없습니다."}</span></div></>
            ) : (
              <><CircleOff size={18} /><div><strong>Windows 3060 Ti 연결 필요</strong><span>워커와 ComfyUI가 준비되면 이 모드에서 대기 작업을 실행할 수 있습니다.</span></div></>
            )
          ) : status?.batch ? (
            status.batch.status === "requested" ? (
              <><Clock3 size={18} /><div><strong>Vast 5090 시작 대기</strong><span>{status.batch.job_count}개 보관 중{status.counts.ready ? ` · ${status.counts.ready}개 더 추가 가능` : " · 소유자 Mac에서 h3-up을 실행하세요."}</span></div></>
            ) : (
              <><LoaderCircle className="spin" size={18} /><div><strong>배치 생성 중</strong><span>{batchJobs.filter((job) => job.status === "completed").length}/{status.batch.job_count} 완료{currentBatchJob && <> · {currentBatchJob.name}<JobTimingLabel job={currentBatchJob} separator /></>}{status.counts.waiting ? ` · 다음 배치 ${status.counts.waiting}개 대기` : " · 대기열이 비면 직접 h3-down"}</span></div></>
            )
          ) : (
            <><Clock3 size={18} /><div><strong>{status?.counts.ready || 0}개 실행 준비</strong><span>등록은 무료입니다. h3-up으로 Vast 5090을 켠 뒤 h3-down까지 과금됩니다.</span></div></>
          )}
        </div>
        <div className="rail-actions">
          <button
            className="button subtle run-button realism-scan-button"
            onClick={upgradeWaitingRealism}
            disabled={realismScanBusy || !(status?.counts.realism_candidates)}
            aria-label="대기열의 안전한 작업을 감지해 현실감 개선 요청"
          >
            {realismScanBusy || status?.counts.realism_pending ? <LoaderCircle className="spin" size={18} /> : <Sparkles size={18} />}
            {realismScanBusy
              ? "대기열 확인 중"
              : status?.counts.realism_candidates
                ? `${status.counts.realism_candidates}개 현실감 점검`
                : status?.counts.realism_pending
                  ? `${status.counts.realism_pending}개 개선 중`
                  : "점검할 작업 없음"}
          </button>
          <button className="button primary run-button" onClick={runBatch} disabled={busy || (executionMode === "windows" ? (!windowsWorkerReady || !selectedWindowsJobs.length) : !(status?.counts.ready))}>
            {busy ? <LoaderCircle className="spin" size={18} /> : <ListPlus size={18} />}
            {executionMode === "windows"
              ? (selectedWindowsJobs.length ? `${selectedWindowsJobs.length}개 선택 작업 시작` : windowsCandidates ? "대기 작업을 선택하세요" : windowsWorkerReady ? "실행할 작업 없음" : "3060 Ti 연결 대기")
              : status?.batch?.status === "running" ? `${status.counts.ready}개 다음 배치에 추가` : status?.batch ? (status.counts.ready ? `${status.counts.ready}개 대기열에 추가` : "GPU 시작 대기") : `${status?.counts.ready || 0}개 대기열에 넣기`}
          </button>
        </div>
      </footer>

      {newOpen && <NewJobDialog onClose={() => setNewOpen(false)} onCreated={(job) => {
        setNewOpen(false);
        setSelectedId(job.id);
        announce(job.status === "queued"
          ? "새 작업을 현재 생성 뒤 이어질 다음 배치에 바로 추가했습니다."
          : "새 영상 작업을 만들었습니다.");
        void refresh();
      }} />}
      {socialAccountsOpen && <SocialAccountsManager
        accounts={socialAccounts}
        onAccountsChange={setSocialAccounts}
        onClose={() => setSocialAccountsOpen(false)}
        announce={announce}
      />}
      {loadError && <div className="toast error" role="alert"><AlertTriangle size={17} />{loadError}<button onClick={() => setLoadError("")} aria-label="닫기"><X size={16} /></button></div>}
      {message && <div className="toast" role="status"><Check size={17} />{message}</div>}
    </div>
  );
}
